import { useGetCurrentUserQuery, useSwitchPortfolioMutation } from '@/api/authApi';
import { useGetPortfoliosQuery } from '@/api/portfolioApi';
import { BookOutlined, CheckOutlined, SearchOutlined } from '@ant-design/icons';
import {
  Button,
  Divider,
  Empty,
  Flex,
  Input,
  message,
  Modal,
  Space,
  Spin,
  Tag,
  Typography,
} from 'antd';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './SelectPortfolioModal.less';

const { Text } = Typography;

const SelectPortfolioModal = ({ open, onCancel, onSelect, currentPortfolioId }) => {
  const navigate = useNavigate();
  const [selectedId, setSelectedId] = useState(currentPortfolioId);
  const [searchValue, setSearchValue] = useState('');

  const {
    data: portfoliosResponse,
    isLoading,
    isFetching,
  } = useGetPortfoliosQuery({ size: 0 }, {
    skip: !open,
  });
  const portfoliosList = portfoliosResponse?.data || portfoliosResponse || [];

  const [switchPortfolio, { isLoading: isSwitching }] = useSwitchPortfolioMutation();
  const { refetch: refetchCurrentUser } = useGetCurrentUserQuery();

  useEffect(() => {
    if (open) {
      setSelectedId(currentPortfolioId);
      setSearchValue('');
    }
  }, [open, currentPortfolioId]);

  const handleSwitch = async () => {
    const selectedPortfolio = portfoliosList.find(
      (portfolio) => portfolio.portfolioId === selectedId
    );
    if (selectedPortfolio) {
      try {
        await switchPortfolio(selectedId).unwrap();
        // Refetch user data to get the new portfolioId before navigating
        await refetchCurrentUser();
        message.success(`Switched to "${selectedPortfolio.portfolioName}"`);
        onSelect?.(selectedPortfolio);
        onCancel();
        navigate('/loading');
      } catch {
        message.error('Failed to switch portfolio. Please try again.');
      }
    }
  };

  const portfolios = useMemo(() => {
    return portfoliosList
      .filter((portfolio) =>
        portfolio.portfolioName?.toLowerCase().includes(searchValue.toLowerCase())
      )
      .slice()
      .sort((a, b) => {
        // Keep inactive portfolios at the bottom of the list.
        const aInactive = a.isActive === false;
        const bInactive = b.isActive === false;
        if (aInactive !== bInactive) {
          return aInactive ? 1 : -1;
        }
        return (a.portfolioName || '').localeCompare(b.portfolioName || '');
      });
  }, [portfoliosList, searchValue]);

  return (
    <Modal
      className="select-portfolio-modal"
      title={
        <Space>
          <BookOutlined />
          Switch Portfolio
        </Space>
      }
      open={open}
      onCancel={onCancel}
      width={450}
      footer={
        <Flex justify="flex-end" gap={8} className="modal-footer">
          <Button onClick={onCancel} disabled={isSwitching}>
            Cancel
          </Button>
          <Button
            type="primary"
            onClick={handleSwitch}
            disabled={selectedId === currentPortfolioId}
            loading={isSwitching}
          >
            Switch
          </Button>
        </Flex>
      }
    >
      <Input
        placeholder="Search portfolios..."
        prefix={<SearchOutlined className="search-icon" />}
        value={searchValue}
        onChange={(e) => setSearchValue(e.target.value)}
        allowClear
      />
      <Divider className="divider" />
      {isLoading || isFetching ? (
        <Flex justify="center" align="center" className="loading-state">
          <Spin />
        </Flex>
      ) : (
        <>
          <Text type="secondary" className="portfolio-count">
            {portfolios.length} portfolio{portfolios.length !== 1 ? 's' : ''} available
          </Text>
          <Flex vertical gap={6} className="portfolio-list">
            {portfolios.length === 0 ? (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="No portfolios found"
                className="empty-state"
              />
            ) : (
              portfolios.map((portfolio) => {
                const isSelected = portfolio.portfolioId === selectedId;
                const isCurrent = portfolio.portfolioId === currentPortfolioId;
                const isInactive = portfolio.isActive === false;
                return (
                  <Flex
                    key={portfolio.portfolioId}
                    align="center"
                    justify="space-between"
                    onClick={() => setSelectedId(portfolio.portfolioId)}
                    className={`portfolio-item ${isSelected ? 'portfolio-item--selected' : ''} ${
                      isInactive ? 'portfolio-item--inactive' : ''
                    }`}
                  >
                    <Flex align="center" gap={8}>
                      <span className="portfolio-name">{portfolio.portfolioName}</span>
                      {isCurrent && <Tag color="red">Current</Tag>}
                      {isInactive && <Tag>Inactive</Tag>}
                    </Flex>
                    {isSelected && <CheckOutlined className="check-icon" />}
                  </Flex>
                );
              })
            )}
          </Flex>
        </>
      )}
    </Modal>
  );
};

export default SelectPortfolioModal;
